import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Sélectionner...",
  className,
  maxDisplay = 2,
  ...props
}) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleRemove = (value, e) => {
    e.stopPropagation();
    onChange(selected.filter((item) => item !== value));
  };

  const displayedItems = selected.slice(0, maxDisplay);
  const remainingCount = selected.length - maxDisplay;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-9 px-3 font-normal",
            selected.length === 0 && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-1 flex-wrap overflow-hidden">
            {selected.length === 0 ? (
              <span>{placeholder}</span>
            ) : (
              <>
                {displayedItems.map((value) => {
                  const option = options.find((opt) => opt.value === value);
                  return (
                    <Badge
                      key={value}
                      variant="secondary"
                      className="h-5 px-1.5 text-xs font-normal"
                    >
                      {option?.label || value}
                      <button
                        className="ml-1 rounded-full outline-none hover:bg-slate-300"
                        onClick={(e) => handleRemove(value, e)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
                {remainingCount > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs font-normal">
                    +{remainingCount}
                  </Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="max-h-60 overflow-auto p-1">
          {options.map((option) => (
            <div
              key={option.value}
              className={cn(
                "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-slate-100",
                selected.includes(option.value) && "bg-slate-100"
              )}
              onClick={() => handleSelect(option.value)}
            >
              <div
                className={cn(
                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                  selected.includes(option.value)
                    ? "bg-primary text-primary-foreground"
                    : "opacity-50"
                )}
              >
                {selected.includes(option.value) && (
                  <Check className="h-3 w-3" />
                )}
              </div>
              {option.label}
            </div>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t p-1">
            <button
              className="w-full rounded-sm px-2 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => onChange([])}
            >
              Tout effacer
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
